import { API_BASE } from "./api";

const parseJsonSafely = async (res) => {
  const text = await res.text().catch(() => "");
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch (err) {
    throw new Error(`Failed to parse JSON (${res.url || "response"}): ${err.message}`);
  }
};

const buildAuthHeaders = (token) =>
  token ? { Authorization: `Bearer ${token}` } : {};

export async function getProductCosts({ authToken, shopId } = {}) {
  const url = new URL(`${API_BASE}/shopify/product-costs`);
  if (shopId) url.searchParams.set("shop_id", shopId);

  const res = await fetch(url.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(authToken),
    },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Product costs request failed (${res.status}): ${text || res.statusText}`
    );
  }

  return parseJsonSafely(res);
}

export async function updateProductCost({ productId, productCost, authToken }) {
  if (!productId) throw new Error("productId is required.");

  const res = await fetch(`${API_BASE}/shopify/product-costs/${productId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(authToken),
    },
    body: JSON.stringify({ product_cost: productCost }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Product cost update failed (${res.status}): ${text || res.statusText}`
    );
  }

  return parseJsonSafely(res);
}

export async function updateProductPrice({ productId, price, authToken }) {
  if (!productId) throw new Error("productId is required.");

  const res = await fetch(`${API_BASE}/shopify/product-prices/${productId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(authToken),
    },
    body: JSON.stringify({ price }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Product price update failed (${res.status}): ${text || res.statusText}`
    );
  }

  return parseJsonSafely(res);
}

export async function uploadProductCostsFile({ file, authToken, shopId }) {
  if (!file) throw new Error("file is required.");
  const contentType =
    file.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  const fileName = file.name || "product-costs.xlsx";

  const presignRes = await fetch(`${API_BASE}/shopify/product-costs/upload/presign`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(authToken),
    },
    body: JSON.stringify({
      fileName,
      contentType,
      sizeBytes: file.size ?? null,
      shopId: shopId || null,
    }),
  });

  if (!presignRes.ok) {
    const text = await presignRes.text().catch(() => "");
    throw new Error(
      `Product cost presign failed (${presignRes.status}): ${text || presignRes.statusText}`
    );
  }

  const presign = await parseJsonSafely(presignRes);
  if (!presign?.uploadUrl || !presign?.key) {
    throw new Error("Presign response missing upload URL or key.");
  }

  const uploadRes = await fetch(presign.uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": contentType,
    },
    body: file,
  });

  if (!uploadRes.ok) {
    throw new Error(`S3 upload failed (${uploadRes.status})`);
  }

  const parseRes = await fetch(`${API_BASE}/shopify/product-costs/upload/parse`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(authToken),
    },
    body: JSON.stringify({
      s3_key: presign.key,
      s3_bucket: presign.bucket || null,
      shop_id: shopId || null,
    }),
  });

  if (!parseRes.ok) {
    const text = await parseRes.text().catch(() => "");
    throw new Error(
      `Product cost parse failed (${parseRes.status}): ${text || parseRes.statusText}`
    );
  }

  return parseJsonSafely(parseRes);
}
